/**
 * Fórmula canónica del saldo de una cuenta.
 *
 * ESTE ARCHIVO TIENE UN GEMELO: `backend/app/core/saldo.py`. Los dos
 * implementan exactamente el mismo cálculo, porque el backend necesita
 * persistir el saldo y el frontend necesita mostrarlo sin esperar al servidor.
 *
 * **Si cambias uno, cambia el otro.** No es una recomendación: hubo un periodo
 * en que divergieron y 14 de 23 cuentas mostraban una cifra distinta según la
 * pantalla, con diferencias de hasta $176,740.
 * `backend/tests/test_saldo_paridad.py` ejecuta ambas implementaciones sobre
 * los mismos vectores y falla si difieren.
 *
 * Las reglas que codifica, y por qué:
 *
 * - **Solo cuentan los fijos PAGADOS.** Un gasto fijo presupuestado y no pagado
 *   todavía no salió de la cuenta.
 * - **Los ghosts de recurrentes no cuentan.** Son proyecciones que se inyectan
 *   en memoria (id `rec_*`) y nunca se persisten. Ojo: un recurrente ya
 *   materializado por el backend tiene `esRecurrente: true` pero UUID real y
 *   **sí** es dinero movido — por eso el criterio es el id, no la bandera.
 * - **En una cuenta de crédito el saldo ES la deuda**, y `saldoInicial`
 *   representa la deuda preexistente al registrar la tarjeta.
 * - **Las transferencias mueven dinero en ambos sentidos.** En una tarjeta, una
 *   transferencia entrante es un pago: reduce la deuda.
 */

export const TIPO_CREDITO = "crédito";

/** ¿Es una proyección en memoria y no un movimiento real? Gemelo de `es_ghost`. */
export function esGhost(item) {
  return String(item?.id ?? "").startsWith("rec_");
}

const suma = (items, campo = "monto") =>
  (items || []).reduce(
    (acc, i) => (esGhost(i) ? acc : acc + (Number(i?.[campo]) || 0)),
    0,
  );

/**
 * Saldo de una cuenta. Los movimientos deben venir ya acotados a la ventana de
 * cálculo (año en curso hasta el mes actual) y a esta cuenta.
 *
 * `fijos` acepta todos los del periodo: aquí se filtran los pagados y se usa
 * `monto_pagado` si existe, cayendo a `monto` si no.
 *
 * `cuotasMsi` solo aplica a cuentas de crédito; se ignora en el resto.
 */
export function calcularSaldo({
  tipo,
  saldoInicial = 0,
  ingresos = [],
  extras = [],
  fijos = [],
  transferEntrante = 0,
  transferSaliente = 0,
  cuotasMsi = 0,
} = {}) {
  const base = Number(saldoInicial) || 0;
  const totalIngresos = suma(ingresos);
  const totalExtras = suma(extras);
  const totalFijos = (fijos || []).reduce((acc, f) => {
    if (!f?.pagado || esGhost(f)) return acc;
    const monto =
      f.monto_pagado !== undefined && f.monto_pagado !== null
        ? f.monto_pagado
        : f.monto;
    return acc + (Number(monto) || 0);
  }, 0);

  const gastos = totalExtras + totalFijos;
  const tin = Number(transferEntrante) || 0;
  const tout = Number(transferSaliente) || 0;

  const saldo =
    tipo === TIPO_CREDITO
      ? base + gastos + (Number(cuotasMsi) || 0) - totalIngresos - tin + tout
      : base + totalIngresos - gastos + tin - tout;

  return Math.round(saldo * 100) / 100;
}
